/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// NOTE: we used to disable #customily-cart-btn on its first click (double-submit
// guard) and set a _customilyCartSubmitInFlight flag for the variant-reload logic.
// Both are gone:
//   * The variant-reload logic was removed (see the comment further down), so the
//     flag had no readers left.
//   * The disable was permanent — the listener was {once:true} and nothing ever
//     re-enabled the button. When Customily rejects the add because a required
//     personalization field is empty, the click still ran our handler, so fixing
//     the field left the customer with a dead button and no way to add to cart.
// Customily's own script already blocks the add while required fields are missing
// and manages the button's enabled state, so no guard is needed here.

// ── Specifications tab — update table on variant change ───────────────────
function _specsEscHtml(str) {
  return String(str == null ? '' : str)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function _buildSpecsTable(s) {
  var rows = [
    ['Manufacturer', 'Carstens']
  ];
  if (s.sku)               rows.push(['Manufacturer Part Number', s.sku]);
  if (s.barcode) {
                            rows.push(['UPC',  s.barcode]);
                            rows.push(['GTIN', '00' + s.barcode]);
  }
  if (s.erp_status)        rows.push(['Item Status',       s.erp_status]);
  if (s.unit_of_measure)   rows.push(['Unit Of Measure',   s.unit_of_measure]);
  if (s.unit_count)        rows.push(['Unit Count',        s.unit_count]);
  if (s.hex_color) {
    var swatchHtml = s.hex_color !== 'Multicolor'
      ? '<span class="specs-color-swatch" style="background-color:' + _specsEscHtml(s.hex_color) + ';"></span>'
      : '';
    rows.push(['Hex Color Code', { raw: swatchHtml + _specsEscHtml(s.hex_color) }]);
  }
  if (s.hs_code)           rows.push(['HS Code',           s.hs_code]);
  if (s.country_of_origin === 'US') rows.push(['Country Of Origin', 'Proudly made in the USA of US and imported parts']);

  return '<table class="specs-table"><tbody>' +
    rows.map(function(r) {
      var value = r[1] && typeof r[1] === 'object' ? r[1].raw : _specsEscHtml(r[1]);
      return '<tr>' +
        '<td class="specs-table__label">' + _specsEscHtml(r[0]) + '</td>' +
        '<td class="specs-table__value">'  + value + '</td>' +
        '</tr>';
    }).join('') +
    '</tbody></table>';
}

document.addEventListener('variant:changed', function(event) {
  var variant = event.detail && event.detail.variant;
  if (!variant) return;

  var specsDataEl = document.querySelector('script[id^="variant-specs-"]');
  var specsWrapper = document.querySelector('[id^="specs-table-wrapper-"]');
  if (!specsDataEl || !specsWrapper) return;

  var data;
  try { data = JSON.parse(specsDataEl.textContent); } catch(e) { return; }

  var specs = data.variants[String(variant.id)];
  if (!specs) return;

  specsWrapper.innerHTML = _buildSpecsTable(specs);
});
// ──────────────────────────────────────────────────────────────────────────

// Customily's own script already reacts to variant changes internally (it polls the
// selected variant id and re-binds text/image/color/price functions on change — see
// their bundle's variationsChangeHandler). Reloading the whole script here was
// redundant and raced with that internal handling: tearing down the modal/script
// while a reload was still in flight (or while an add-to-cart click was in progress)
// left window.customilyShopify half-initialized and the add silently no-op'd.
var CUSTOMILY_DEBUG = true; // set to false to silence logs
var clog = function() { if (CUSTOMILY_DEBUG) console.log.apply(console, ['[CSTMLY]'].concat(Array.prototype.slice.call(arguments))); };
var cwarn = function() { if (CUSTOMILY_DEBUG) console.warn.apply(console, ['[CSTMLY]'].concat(Array.prototype.slice.call(arguments))); };
var cerr = function() { if (CUSTOMILY_DEBUG) console.error.apply(console, ['[CSTMLY]'].concat(Array.prototype.slice.call(arguments))); };

// Surface any runtime error thrown by the Customily script/app-block itself, since those
// otherwise show up in Shopify's error tracking as generic "Script error" with no source.
window.addEventListener('error', function(event) {
  var src = event.filename || (event.target && event.target.src) || '';
  if (typeof src === 'string' && src.indexOf('customily') !== -1) {
    cerr('uncaught error from Customily script:', event.message, 'at', src + ':' + event.lineno + ':' + event.colno, event.error);
  }
}, true);

window.addEventListener('unhandledrejection', function(event) {
  var reason = event.reason;
  var text = (reason && (reason.stack || reason.message)) || String(reason || '');
  if (text.indexOf('customily') !== -1 || text.indexOf('Customily') !== -1) {
    cerr('unhandled promise rejection referencing Customily:', reason);
  }
});

// Log the Customily properties Shopify actually captured on cart line items, so a
// customer complaint ("my design didn't save") can be checked against what data made
// it into the cart vs. what Customily's UI showed.
document.addEventListener('cart:updated', function(event) {
  var cart = event.detail && event.detail.cart;
  if (!cart || !cart.items) return;

  cart.items.forEach(function(item, index) {
    var props = item.properties || {};
    var customilyKeys = Object.keys(props).filter(function(key) { return key.indexOf('_customily') === 0; });
    if (customilyKeys.length) {
      var snapshot = {};
      customilyKeys.forEach(function(key) { snapshot[key] = props[key]; });
      clog('cart item', index, '(' + item.product_title + ') has Customily properties:', snapshot);
    } else {
      clog('cart item', index, '(' + item.product_title + ') has NO Customily properties');
    }
  });
});

clog('Customily logging initialized. Script tag present:', !!document.querySelector('script[src*="customily.shopify.script.js"]'), '| window.customilyShopify:', window.customilyShopify);